// src/components/navigation/LeftSidebar.jsx
import React, { useState, useEffect, useRef, useCallback, useMemo, memo } from "react";
import { NavLink, Link, useNavigate } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import { useTranslation } from "react-i18next";
import {
  FiBell, FiPackage, FiTag, FiTarget, FiPlusSquare, FiSettings,
  FiSearch, FiX, FiMenu, FiChevronDown, FiChevronRight, FiUser,
  FiLogOut, FiBriefcase, FiUsers, FiDollarSign, FiClock, FiFileText,
  FiHome, FiCheckSquare, FiMessageSquare, FiCalendar, FiGrid,
  FiHelpCircle, FiPlay, FiRepeat, FiZap, FiShield, FiCreditCard,
  FiLayers, FiBook, FiClipboard, FiTruck, FiInbox, FiMapPin,
  FiDownloadCloud, FiGlobe, FiTrendingUp, FiEdit, FiMail, FiLink,
} from "react-icons/fi";
import {
  doc, getDoc, collection, query, where, onSnapshot,
  updateDoc, orderBy, limit,
} from "firebase/firestore";
import { auth, db } from "../../firebase";
import { useAuth } from "../../context/AuthContext";
import { useFeatureAccess } from "../../hooks/useFeatureAccess";
import { FEATURES } from "../../utils/subscriptionPlans";
import { formatDate as formatDateUtil, getBrowserTimezone } from "../../utils/dateUtils";
import LanguageSelector from "../common/LanguageSelector";
import ThemeToggle from "../common/ThemeToggle";
import Logo from "../../assets/Image/Effitrack.png";
import BusinessLogo from "../../assets/Image/businessLogo.png";

// ─────────────────────────────────────────────────────────────
// CONSTANTS
// ─────────────────────────────────────────────────────────────
const BRAND_GRADIENT = "linear-gradient(135deg,#0D3B66,#14B8A6)";

const PLAN_STYLES = {
  free:    { bg: "#F1F5F9", color: "#64748B", label: "Free" },
  starter: { bg: "#ECFDF5", color: "#059669", label: "Starter" },
  growth:  { bg: "#EFF6FF", color: "#2563EB", label: "Growth" },
  scale:   { bg: BRAND_GRADIENT,             color: "#fff",    label: "Scale" },
};

const LS_COLLAPSED = "effitrack:sidebar:collapsed";
const LS_EXPANDED  = "effitrack:sidebar:expanded";

const readLS = (key, fallback) => {
  try {
    const raw = localStorage.getItem(key);
    return raw == null ? fallback : JSON.parse(raw);
  } catch {
    return fallback;
  }
};
const writeLS = (key, value) => {
  try { localStorage.setItem(key, JSON.stringify(value)); } catch {}
};

// ─────────────────────────────────────────────────────────────
// NOTIFICATION TYPE → ICON + TINT
// ─────────────────────────────────────────────────────────────
const notifVisuals = (type = "") => {
  if (type.startsWith("task"))            return { Icon: FiCheckSquare,  tint: "from-violet-500 to-indigo-500" };
  if (type.startsWith("booking"))         return { Icon: FiCalendar,     tint: "from-sky-500 to-cyan-500" };
  if (type.startsWith("estimate") || type === "deposit_paid")
                                          return { Icon: FiClipboard,    tint: "from-amber-500 to-orange-500" };
  if (type.startsWith("invoice") || type.startsWith("payment"))
                                          return { Icon: FiDollarSign,   tint: "from-emerald-500 to-teal-500" };
  if (type.startsWith("contract"))        return { Icon: FiEdit,         tint: "from-slate-500 to-slate-700" };
  if (type.startsWith("proposal"))        return { Icon: FiBriefcase,    tint: "from-blue-500 to-indigo-600" };
  if (type.startsWith("quote"))           return { Icon: FiTag,          tint: "from-pink-500 to-rose-500" };
  if (type.startsWith("bill_of_sale"))    return { Icon: FiFileText,     tint: "from-fuchsia-500 to-pink-500" };
  if (type === "new_request")             return { Icon: FiInbox,        tint: "from-teal-500 to-cyan-500" };
  if (type.startsWith("employee"))        return { Icon: FiUsers,        tint: "from-purple-500 to-fuchsia-500" };
  if (type === "reminder" || type === "recurring_job_due")
                                          return { Icon: FiRepeat,       tint: "from-orange-500 to-red-500" };
  if (type === "low_inventory")           return { Icon: FiPackage,      tint: "from-rose-500 to-red-500" };
  if (type === "customer_birthday" || type === "customer_anniversary")
                                          return { Icon: FiTag,          tint: "from-pink-400 to-rose-500" };
  if (type === "timesheet_approved")      return { Icon: FiClock,        tint: "from-emerald-500 to-green-500" };
  if (type === "expense_submitted")       return { Icon: FiDollarSign,   tint: "from-amber-500 to-yellow-500" };
  if (type === "review_received")         return { Icon: FiTrendingUp,   tint: "from-yellow-500 to-amber-500" };
  return { Icon: FiBell, tint: "from-[#0D3B66] to-[#14B8A6]" };
};

// ─────────────────────────────────────────────────────────────
// NOTIFICATION ROUTE HELPER
// ─────────────────────────────────────────────────────────────
export const getNotificationRoute = (notif) => {
  const { type, metadata } = notif;
  switch (type) {
    case "task":
    case "task_completed":
      return metadata?.taskId
        ? `/admin/tasks?taskId=${metadata.taskId}`
        : metadata?.bookingId
        ? `/admin/booking/${metadata.bookingId}`
        : "/admin/tasks";
    case "booking":
    case "booking_created":
    case "booking_confirmed":
    case "booking_cancelled":
    case "booking_reminder":
      return metadata?.bookingId ? `/admin/booking/${metadata.bookingId}` : "/admin/bookings";
    case "estimate":
    case "estimate_accepted":
    case "estimate_rejected":
    case "estimate_expiring":
    case "estimate_signed":
    case "estimate_declined":
    case "estimate_viewed":
    case "deposit_paid":
      return metadata?.estimateId ? `/admin/estimates/${metadata.estimateId}` : "/admin/estimates";
    case "invoice":
    case "invoice_paid":
    case "invoice_overdue":
    case "invoice_signed":
    case "invoice_declined":
    case "invoice_viewed":
    case "payment":
    case "payment_received":
    case "payment_failed":
      return metadata?.invoiceId ? `/admin/invoice/${metadata.invoiceId}` : "/admin/invoices";
    case "contract_signed":
    case "contract_declined":
    case "contract_viewed":
      return metadata?.contractId ? `/admin/contracts/${metadata.contractId}` : "/admin/contracts";
    case "quote_signed":
    case "quote_declined":
    case "quote_viewed":
      return metadata?.quoteId ? `/admin/quotes/${metadata.quoteId}` : "/admin/quotes";
    case "proposal_signed":
    case "proposal_declined":
    case "proposal_viewed":
      return metadata?.proposalId ? `/admin/proposals/${metadata.proposalId}` : "/admin/proposals";
    case "bill_of_sale_signed":
    case "bill_of_sale_declined":
    case "bill_of_sale_viewed":
      return metadata?.billOfSaleId ? `/admin/bills-of-sale/${metadata.billOfSaleId}` : "/admin/bills-of-sale";
    case "new_request":
      return metadata?.requestId ? `/admin/requests/${metadata.requestId}` : "/admin/requests";
    case "employee":
    case "employee_message":
    case "employee_clock_in":
      return "/admin/employees";
    case "reminder":
    case "recurring_job_due":
      return "/admin/recurring-jobs";
    case "low_inventory":
      return "/admin/services";
    case "customer_birthday":
    case "customer_anniversary":
      return "/admin/customers";
    case "timesheet_approved":
      return "/admin/timesheet";
    case "expense_submitted":
      return "/admin/expenses";
    case "review_received":
      return "/admin/marketing";
    default:
      return "/admin/notifications";
  }
};

// ─────────────────────────────────────────────────────────────
// TOOLTIP (delayed, a11y)
// ─────────────────────────────────────────────────────────────
const Tooltip = memo(({ label, children }) => {
  const [show, setShow] = useState(false);
  const timer = useRef(null);
  const id = useRef(`tt-${Math.random().toString(36).slice(2, 9)}`);

  const open  = () => { clearTimeout(timer.current); timer.current = setTimeout(() => setShow(true), 250); };
  const close = () => { clearTimeout(timer.current); setShow(false); };

  useEffect(() => () => clearTimeout(timer.current), []);

  return (
    <div
      className="relative flex"
      onMouseEnter={open}
      onMouseLeave={close}
      onFocus={open}
      onBlur={close}
      aria-describedby={show ? id.current : undefined}
    >
      {children}
      {show && (
        <div
          id={id.current}
          role="tooltip"
          className="absolute left-full top-1/2 -translate-y-1/2 ml-3 z-50 pointer-events-none"
        >
          <div className="bg-slate-900 dark:bg-slate-800 text-white text-xs font-semibold px-2.5 py-1.5 rounded-lg whitespace-nowrap shadow-xl">
            {label}
            <div className="absolute right-full top-1/2 -translate-y-1/2 border-4 border-transparent border-r-slate-900 dark:border-r-slate-800" />
          </div>
        </div>
      )}
    </div>
  );
});

// ─────────────────────────────────────────────────────────────
// NAV ITEM
// ─────────────────────────────────────────────────────────────
const NavItem = memo(({ item, collapsed, onClose, hasFeatureAccess }) => {
  const proLabel = item.proLabel || "PRO";
  const el = (
    <NavLink
      to={item.to}
      onClick={onClose}
      className={({ isActive }) =>
        `group relative flex items-center gap-2.5 rounded-xl transition-all duration-150 font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-400
        ${collapsed ? "justify-center p-2.5" : "px-3 py-2.5"}
        ${isActive
          ? "bg-gradient-to-r from-[#0D3B66] to-[#14B8A6] text-white shadow-lg shadow-[#0D3B66]/20"
          : "text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-800 dark:hover:text-slate-100"
        }
        ${!hasFeatureAccess ? "opacity-60" : ""}`
      }
    >
      {({ isActive }) => (
        <>
          {/* Active inset bar */}
          {isActive && !collapsed && (
            <span className="absolute left-1 top-1/4 h-1/2 w-0.5 rounded-full bg-white/80" />
          )}

          <item.icon className="w-[17px] h-[17px] flex-shrink-0" />

          {!collapsed && (
            <>
              <span className="flex-1 text-[13px] leading-none">{item.label}</span>

              {/* Unread badge — pulses only when count is small */}
              {item.badge > 0 && (
                <span
                  className={`bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full leading-none ${
                    item.badge <= 3 ? "animate-pulse" : ""
                  }`}
                >
                  {item.badge > 99 ? "99+" : item.badge}
                </span>
              )}

              {/* PRO badge */}
              {!hasFeatureAccess && (
                <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-gradient-to-r from-[#0D3B66] to-[#14B8A6] text-white tracking-wide">
                  {proLabel}
                </span>
              )}
            </>
          )}

          {/* Collapsed unread dot */}
          {collapsed && item.badge > 0 && (
            <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-red-500 border-2 border-white dark:border-slate-900" />
          )}
        </>
      )}
    </NavLink>
  );

  return collapsed ? <Tooltip label={item.label}>{el}</Tooltip> : el;
});

// ─────────────────────────────────────────────────────────────
// SECTION GROUP
// ─────────────────────────────────────────────────────────────
const NavGroup = memo(({ group, expanded, onToggle, collapsed, onClose, hasAccess, comingSoonLabel }) => {
  if (!group.group) {
    return (
      <div className="space-y-0.5">
        {group.items.map((item) =>
          item.comingSoon ? (
            collapsed ? (
              <Tooltip key={item.to} label={`${item.label} — ${comingSoonLabel}`}>
                <div className="flex justify-center p-2.5 rounded-xl text-slate-300 dark:text-slate-600 cursor-default">
                  <item.icon className="w-[17px] h-[17px] flex-shrink-0" />
                </div>
              </Tooltip>
            ) : (
              <div
                key={item.to}
                className="group relative flex items-center gap-2.5 rounded-xl px-3 py-2.5 text-slate-300 dark:text-slate-600 cursor-default select-none"
              >
                <item.icon className="w-[17px] h-[17px] flex-shrink-0" />
                <span className="flex-1 text-[13px] leading-none">{item.label}</span>
                <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-slate-400 dark:text-slate-500 tracking-wide">
                  {comingSoonLabel}
                </span>
              </div>
            )
          ) : (
            <NavItem
              key={item.to}
              item={item}
              collapsed={collapsed}
              onClose={onClose}
              hasFeatureAccess={!item.feature || hasAccess(item.feature)}
            />
          )
        )}
      </div>
    );
  }

  const GroupIcon = group.icon;

  return (
    <div className="mt-1">
      {collapsed ? (
        <Tooltip label={group.group}>
          <button
            onClick={onToggle}
            className="w-full flex justify-center p-2 rounded-lg text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300 transition-colors"
          >
            <GroupIcon className="w-4 h-4" />
          </button>
        </Tooltip>
      ) : (
        <button
          onClick={onToggle}
          data-section={group.key}
          aria-expanded={expanded}
          className="w-full flex items-center justify-between px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 transition-colors rounded-lg"
        >
          <span>{group.group}</span>
          <FiChevronDown
            className={`w-3.5 h-3.5 transition-transform duration-200 ${expanded ? "rotate-180" : ""}`}
          />
        </button>
      )}

      {(expanded || collapsed) && (
        <div
          className={`space-y-0.5 mt-0.5 ${
            collapsed ? "" : "pl-1 border-l-2 border-slate-100 dark:border-slate-800 ml-2"
          }`}
        >
          {group.items.map((item) => (
            <NavItem
              key={item.to}
              item={item}
              collapsed={collapsed}
              onClose={onClose}
              hasFeatureAccess={!item.feature || hasAccess(item.feature)}
            />
          ))}
        </div>
      )}
    </div>
  );
});

// ─────────────────────────────────────────────────────────────
// NOTIFICATION PANEL
// ─────────────────────────────────────────────────────────────
const NotificationPanel = memo(({
  notifications, bizId, onClose, onNavigate, formatTime, strings,
}) => {
  const unreadCount = notifications.filter((n) => !n.read).length;

  const markRead = async (notif) => {
    if (!notif.read && bizId) {
      try {
        await updateDoc(doc(db, `businesses/${bizId}/notifications`, notif.id), { read: true });
      } catch (err) {
        console.error("Error marking notification as read:", err);
      }
    }
    onNavigate(getNotificationRoute(notif));
    onClose();
  };

  const markAll = async () => {
    if (!bizId) return;
    const unread = notifications.filter((n) => !n.read);
    await Promise.allSettled(
      unread.map((n) =>
        updateDoc(doc(db, `businesses/${bizId}/notifications`, n.id), { read: true })
      )
    );
  };

  return (
    <div
      role="dialog"
      aria-label={strings.title}
      className="absolute right-0 top-full mt-2 w-80 bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-700 overflow-hidden z-50"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 dark:border-slate-800">
        <div>
          <p className="text-sm font-bold text-slate-800 dark:text-slate-100">{strings.title}</p>
          <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5">
            {unreadCount} {strings.unread}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {unreadCount > 0 && (
            <button
              onClick={markAll}
              className="text-xs font-semibold text-teal-600 hover:text-teal-700 dark:text-teal-300 dark:hover:text-teal-200 transition-colors"
            >
              {strings.markAll}
            </button>
          )}
          <button
            onClick={onClose}
            aria-label="Close"
            className="p-1.5 rounded-lg text-slate-400 dark:text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 transition-all"
          >
            <FiX className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* List */}
      <div className="max-h-80 overflow-y-auto divide-y divide-slate-50 dark:divide-slate-800">
        {notifications.length === 0 ? (
          <div className="py-10 text-center">
            <FiBell className="w-8 h-8 mx-auto text-slate-200 dark:text-slate-700 mb-2" />
            <p className="text-sm text-slate-400 dark:text-slate-500">{strings.empty}</p>
          </div>
        ) : (
          notifications.slice(0, 8).map((notif) => {
            const { Icon, tint } = notifVisuals(notif.type);
            return (
              <button
                key={notif.id}
                onClick={() => markRead(notif)}
                className={`w-full text-left flex items-start gap-3 px-4 py-3 transition-colors hover:bg-slate-50 dark:hover:bg-slate-800 ${
                  !notif.read ? "bg-emerald-50/60 dark:bg-emerald-500/10" : ""
                }`}
              >
                <div
                  className={`mt-0.5 w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    notif.read
                      ? "bg-slate-100 dark:bg-slate-800"
                      : `bg-gradient-to-br ${tint}`
                  }`}
                >
                  <Icon className={`w-3.5 h-3.5 ${notif.read ? "text-slate-400 dark:text-slate-500" : "text-white"}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[13px] font-semibold text-slate-800 dark:text-slate-100 leading-snug">{notif.title}</p>
                  {notif.message && (
                    <p className="text-xs text-slate-500 dark:text-slate-300 mt-0.5 line-clamp-2">{notif.message}</p>
                  )}
                  {notif.metadata?.rejectionReason && (
                    <p className="text-xs text-red-500 dark:text-red-300 mt-1 italic bg-red-50 dark:bg-red-500/10 px-2 py-0.5 rounded">
                      "{notif.metadata.rejectionReason}"
                    </p>
                  )}
                  <p className="text-[10px] text-slate-400 dark:text-slate-500 mt-1">{formatTime(notif.timestamp)}</p>
                </div>
                {!notif.read && (
                  <span className="mt-1.5 w-2 h-2 rounded-full bg-teal-400 flex-shrink-0" />
                )}
              </button>
            );
          })
        )}
      </div>

      {/* Footer */}
      <div className="px-4 py-2.5 border-t border-slate-100 dark:border-slate-800 text-center">
        <Link
          to="/admin/notifications"
          onClick={onClose}
          className="text-xs font-semibold text-teal-600 hover:text-teal-700 dark:text-teal-300 dark:hover:text-teal-200 transition-colors"
        >
          {strings.viewAll} →
        </Link>
      </div>
    </div>
  );
});

// ─────────────────────────────────────────────────────────────
// SEARCH PANEL (fuzzy + keyboard nav)
// ─────────────────────────────────────────────────────────────
const scoreMatch = (label, q) => {
  const l = label.toLowerCase();
  const needle = q.toLowerCase();
  if (l === needle) return 1000;
  if (l.startsWith(needle)) return 500;
  // word-boundary
  const words = l.split(/\s+/);
  if (words.some((w) => w.startsWith(needle))) return 300;
  // substring
  const idx = l.indexOf(needle);
  if (idx >= 0) return 100 - idx;
  return -1;
};

const SearchPanel = memo(({ navGroups, onClose, strings }) => {
  const [q, setQ] = useState("");
  const [cursor, setCursor] = useState(0);
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const allItems = useMemo(() => navGroups.flatMap((g) => g.items), [navGroups]);

  useEffect(() => { inputRef.current?.focus(); }, []);

  const results = useMemo(() => {
    const needle = q.trim();
    if (!needle) return [];
    return allItems
      .map((it) => ({ it, score: scoreMatch(it.label, needle) }))
      .filter((x) => x.score >= 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 7)
      .map((x) => x.it);
  }, [allItems, q]);

  useEffect(() => { setCursor(0); }, [q]);

  const onKeyDown = (e) => {
    if (results.length === 0) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setCursor((c) => (c + 1) % results.length); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setCursor((c) => (c - 1 + results.length) % results.length); }
    else if (e.key === "Enter") {
      e.preventDefault();
      const hit = results[cursor];
      if (hit) { navigate(hit.to); onClose(); }
    }
  };

  return (
    <div
      role="dialog"
      aria-label={strings.placeholder}
      className="absolute right-0 top-full mt-2 w-80 bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-700 overflow-hidden z-50"
    >
      <div className="flex items-center gap-2.5 px-3.5 py-3 border-b border-slate-100 dark:border-slate-800">
        <FiSearch className="w-4 h-4 text-slate-400 dark:text-slate-500 flex-shrink-0" />
        <input
          ref={inputRef}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={strings.placeholder}
          className="flex-1 text-sm outline-none bg-transparent text-slate-800 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500"
        />
        <kbd className="hidden sm:inline-flex text-[9px] font-bold tracking-wider px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400">
          ESC
        </kbd>
      </div>

      <div className="p-2">
        {q && results.length === 0 && (
          <p className="text-center text-sm text-slate-400 dark:text-slate-500 py-6">
            {strings.noResults.replace("{q}", q)}
          </p>
        )}

        {results.map((r, i) => (
          <NavLink
            key={r.to}
            to={r.to}
            onClick={onClose}
            className={`flex items-center gap-3 px-3 py-2 rounded-xl transition-colors ${
              i === cursor
                ? "bg-slate-100 dark:bg-slate-800"
                : "hover:bg-slate-50 dark:hover:bg-slate-800/60"
            }`}
          >
            <div className="w-7 h-7 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
              <r.icon className="w-3.5 h-3.5 text-[#0D3B66] dark:text-teal-300" />
            </div>
            <span className="text-[13px] font-semibold text-slate-700 dark:text-slate-200">{r.label}</span>
            <FiChevronRight className="w-3.5 h-3.5 text-slate-300 dark:text-slate-600 ml-auto" />
          </NavLink>
        ))}

        {!q && (
          <>
            <p className="px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-slate-400 dark:text-slate-500">
              {strings.quickAccess}
            </p>
            <div className="flex flex-wrap gap-1.5 px-3 pb-2">
              {strings.quickLabels.map((label) => {
                const item = allItems.find((i) => i.label === label);
                return item ? (
                  <NavLink
                    key={label}
                    to={item.to}
                    onClick={onClose}
                    className="text-xs font-medium px-2.5 py-1 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:border-teal-400 hover:text-teal-600 dark:hover:text-teal-300 transition-colors"
                  >
                    {label}
                  </NavLink>
                ) : null;
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
});

// ─────────────────────────────────────────────────────────────
// PROFILE MENU
// ─────────────────────────────────────────────────────────────
const ProfileMenu = memo(({ admin, businessLogoUrl, onClose, onLogout, strings }) => {
  const navigate = useNavigate();
  const go = (to) => { navigate(to); onClose(); };

  return (
    <div
      role="menu"
      className="absolute right-0 top-full mt-2 w-56 bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-700 overflow-hidden z-50"
    >
      {/* User header */}
      <div className="flex items-center gap-3 px-4 py-3" style={{ background: BRAND_GRADIENT }}>
        <img
          src={businessLogoUrl || BusinessLogo}
          alt="Profile"
          className="w-9 h-9 rounded-full border-2 border-emerald-200 object-cover bg-white"
        />
        <div className="min-w-0">
          <p className="text-sm font-bold text-white truncate">
            {admin?.name || admin?.email?.split("@")[0]}
          </p>
          <p className="text-xs text-white/70">{strings.administrator}</p>
        </div>
      </div>

      <div className="p-1.5 space-y-0.5">
        {strings.items.map((it) => (
          <button
            key={it.to}
            role="menuitem"
            onClick={() => go(it.to)}
            className="w-full flex items-center gap-3 px-3 py-2 rounded-xl text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors text-left"
          >
            <it.icon className="w-4 h-4 text-slate-400 dark:text-slate-500" />
            <span className="text-[13px] font-medium">{it.label}</span>
          </button>
        ))}
        <div className="h-px bg-slate-100 dark:bg-slate-800 my-1" />
        <button
          role="menuitem"
          onClick={onLogout}
          className="w-full flex items-center gap-3 px-3 py-2 rounded-xl text-red-500 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors"
        >
          <FiLogOut className="w-4 h-4" />
          <span className="text-[13px] font-semibold">{strings.logout}</span>
        </button>
      </div>
    </div>
  );
});

// ─────────────────────────────────────────────────────────────
// useSidebarData
// ─────────────────────────────────────────────────────────────
function useSidebarData(setUnreadMessageCount) {
  const [admin, setAdmin]                   = useState(null);
  const [company, setCompany]               = useState("");
  const [businessLogoUrl, setBusinessLogoUrl] = useState(null);
  const [bizId, setBizId]                   = useState(null);
  const [notifications, setNotifications]   = useState([]);
  const [businessTimezone, setBusinessTimezone] = useState(getBrowserTimezone());

  const formatNotificationTime = useCallback(
    (timestamp) => {
      if (!timestamp) return "";
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      const diff = Date.now() - date;
      const m = Math.floor(diff / 60000);
      const h = Math.floor(diff / 3600000);
      const d = Math.floor(diff / 86400000);
      if (m < 1) return "Just now";
      if (m < 60) return `${m}m ago`;
      if (h < 24) return `${h}h ago`;
      if (d < 7) return `${d}d ago`;
      return formatDateUtil(date, { format: "MMM d, yyyy", timezone: businessTimezone });
    },
    [businessTimezone]
  );

  useEffect(() => {
    let mounted = true;
    let unsubMessages = null;
    let unsubNotifications = null;

    const unsubAuth = onAuthStateChanged(auth, async (user) => {
      if (!mounted) return;

      if (!user) {
        setAdmin(null);
        setCompany("");
        setBizId(null);
        setNotifications([]);
        unsubMessages?.();
        unsubNotifications?.();
        return;
      }

      try {
        const udoc = await getDoc(doc(db, "users", user.uid));
        if (!mounted || !udoc.exists()) return;

        const udata = udoc.data();
        setAdmin({ uid: user.uid, ...udata });

        if (!udata.businessId) return;
        setBizId(udata.businessId);

        const bdoc = await getDoc(doc(db, "businesses", udata.businessId));
        if (!mounted || !bdoc.exists()) return;

        const bdata = bdoc.data();
        setCompany(bdata.businessName || "");
        if (bdata.timezone) setBusinessTimezone(bdata.timezone);
        if (bdata.businessPicture) setBusinessLogoUrl(bdata.businessPicture);
        else if (bdata.logo) setBusinessLogoUrl(bdata.logo);

        unsubMessages?.();
        const msgRef = collection(db, `businesses/${udata.businessId}/messages`);
        const msgQ = query(
          msgRef,
          where("participants", "array-contains", user.uid),
          where("read", "==", false)
        );
        unsubMessages = onSnapshot(
          msgQ,
          (snap) => {
            if (!mounted) return;
            const count = snap.docs.filter((d) => d.data().sender !== user.uid).length;
            setUnreadMessageCount(count);
          },
          (err) => console.error("Messages listener error:", err)
        );

        unsubNotifications?.();
        const notifRef = collection(db, `businesses/${udata.businessId}/notifications`);
        const notifQ = query(notifRef, orderBy("timestamp", "desc"), limit(50));
        unsubNotifications = onSnapshot(
          notifQ,
          (snap) => {
            if (!mounted) return;
            setNotifications(
              snap.docs
                .map((d) => ({ id: d.id, ...d.data() }))
                .filter((n) => !n.forEmployee)
            );
          },
          (err) => console.error("Notifications listener error:", err)
        );
      } catch (err) {
        console.error("Sidebar data error:", err);
      }
    });

    return () => {
      mounted = false;
      unsubAuth();
      unsubMessages?.();
      unsubNotifications?.();
    };
  }, [setUnreadMessageCount]);

  return { admin, company, businessLogoUrl, bizId, notifications, businessTimezone, formatNotificationTime };
}

// ─────────────────────────────────────────────────────────────
// SIDEBAR CONTENT
// ─────────────────────────────────────────────────────────────
const SidebarContent = memo(({
  collapsed, onClose, navGroups, expandedSections, toggleSection,
  admin, company, businessLogoUrl, plan, planDisplayName, hasAccess, strings,
}) => (
  <div className="flex flex-col h-full overflow-hidden">
    {/* Logo */}
    <div className={`border-b border-slate-100 dark:border-slate-800 flex-shrink-0 ${collapsed ? "p-3" : "px-4 py-3.5"}`}>
      <Link
        to="/admin/dashboard"
        onClick={onClose}
        className="flex items-center gap-3 focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-400 rounded-xl"
      >
        <div
          className="flex-shrink-0 rounded-xl flex items-center justify-center shadow-md"
          style={{
            width: collapsed ? 36 : 42,
            height: collapsed ? 36 : 42,
            background: BRAND_GRADIENT,
          }}
        >
          <img src={Logo} alt="Effi-Track" className="w-full h-full rounded-xl object-contain" />
        </div>
        {!collapsed && (
          <div>
            <p className="font-black text-[15px] text-[#0D3B66] dark:text-teal-300 leading-none tracking-tight">
              Effi-Track
            </p>
            <p className="text-[10px] text-slate-400 dark:text-slate-500 mt-0.5">{strings.tagline}</p>
          </div>
        )}
      </Link>
    </div>

    {/* Plan badge */}
    {!collapsed && (
      <div className="px-3 pt-3 flex-shrink-0">
        <Link
          to="/admin/subscriptions"
          className="flex items-center justify-between w-full px-3 py-2 rounded-xl transition-all hover:scale-[1.02] hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-400"
          style={{
            background: plan === "scale"
              ? BRAND_GRADIENT
              : PLAN_STYLES[plan]?.bg || "#F1F5F9",
          }}
        >
          <div className="flex items-center gap-1.5">
            <FiZap
              className="w-3.5 h-3.5"
              style={{ color: plan === "scale" ? "#fff" : PLAN_STYLES[plan]?.color }}
            />
            <span
              className="text-[11px] font-bold uppercase tracking-wider"
              style={{ color: plan === "scale" ? "#fff" : PLAN_STYLES[plan]?.color }}
            >
              {planDisplayName} {strings.plan}
            </span>
          </div>
          {plan !== "scale" && (
            <span className="text-[9px] font-bold bg-white text-teal-600 px-2 py-0.5 rounded-full">
              {strings.upgrade}
            </span>
          )}
        </Link>
      </div>
    )}

    {/* Nav */}
    <nav
      aria-label="Primary"
      className="flex-1 overflow-y-auto py-3 px-2 space-y-0.5 sidebar-scroll"
    >
      {navGroups.map((group) => (
        <NavGroup
          key={group.key || "_root"}
          group={group}
          collapsed={collapsed}
          onClose={onClose}
          hasAccess={hasAccess}
          comingSoonLabel={strings.comingSoon}
          expanded={!!expandedSections[group.key]}
          onToggle={() => toggleSection(group.key)}
        />
      ))}

      {/* Quick links — demoted to match nav weight */}
      <div className="pt-2 mt-1 border-t border-slate-100 dark:border-slate-800 space-y-0.5">
        <NavLink
          to="/admin/subscriptions"
          onClick={onClose}
          className={({ isActive }) =>
            `flex items-center gap-2.5 rounded-xl transition-all duration-150
            ${collapsed ? "justify-center p-2.5" : "px-3 py-2.5"}
            ${isActive
              ? "bg-slate-100 dark:bg-slate-800 text-slate-800 dark:text-slate-100"
              : "text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-800 dark:hover:text-slate-100"
            }`
          }
        >
          <FiPlusSquare className="w-[17px] h-[17px] flex-shrink-0" />
          {!collapsed && <span className="text-[13px] font-medium">{strings.subscription}</span>}
        </NavLink>

        <NavLink
          to="/admin/support"
          onClick={onClose}
          className={({ isActive }) =>
            `flex items-center gap-2.5 rounded-xl transition-all duration-150
            ${collapsed ? "justify-center p-2.5" : "px-3 py-2.5"}
            ${isActive
              ? "bg-slate-100 dark:bg-slate-800 text-slate-800 dark:text-slate-100"
              : "text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-800 dark:hover:text-slate-100"
            }`
          }
        >
          <FiHelpCircle className="w-[17px] h-[17px] flex-shrink-0" />
          {!collapsed && <span className="text-[13px] font-medium">{strings.support}</span>}
        </NavLink>

        <NavLink
          to="/admin/getting-started"
          onClick={onClose}
          className={({ isActive }) =>
            `flex items-center gap-2.5 rounded-xl transition-all duration-150
            ${collapsed ? "justify-center p-2.5" : "px-3 py-2.5"}
            ${isActive
              ? "bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
              : "text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-800 dark:hover:text-slate-100"
            }`
          }
        >
          <FiPlay className="w-[17px] h-[17px] flex-shrink-0" />
          {!collapsed && <span className="text-[13px] font-medium">{strings.gettingStarted}</span>}
        </NavLink>

        <NavLink
          to="/training"
          onClick={onClose}
          className={({ isActive }) =>
            `flex items-center gap-2.5 rounded-xl transition-all duration-150
            ${collapsed ? "justify-center p-2.5" : "px-3 py-2.5"}
            ${isActive
              ? "bg-teal-50 dark:bg-teal-500/10 text-teal-700 dark:text-teal-300"
              : "text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-800 dark:hover:text-slate-100"
            }`
          }
        >
          <FiBook className="w-[17px] h-[17px] flex-shrink-0" />
          {!collapsed && <span className="text-[13px] font-medium">{strings.training}</span>}
        </NavLink>
      </div>
    </nav>

    {/* Footer */}
    {admin && (
      <div className={`flex-shrink-0 border-t border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/60 ${collapsed ? "p-2" : "p-3"}`}>
        <div className={`flex items-center gap-2.5 ${collapsed ? "justify-center" : ""}`}>
          <img
            src={businessLogoUrl || BusinessLogo}
            alt="Business"
            className="w-9 h-9 rounded-full border-2 border-emerald-200 dark:border-emerald-500/40 object-cover bg-white flex-shrink-0"
          />
          {!collapsed && (
            <div className="flex-1 min-w-0">
              <p className="text-[13px] font-bold text-slate-800 dark:text-slate-100 truncate leading-none">
                {company || admin.name || admin.email?.split("@")[0]}
              </p>
              <p className="text-[10px] text-slate-400 dark:text-slate-500 mt-0.5">{strings.administrator}</p>
            </div>
          )}
        </div>
      </div>
    )}
  </div>
));

// ─────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────
const LeftSidebar = ({ onCollapsedChange }) => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { hasAccess, planDisplayName, plan } = useFeatureAccess();
  const { t } = useTranslation(["navigation", "common"]);

  // UI state (persisted)
  const [collapsed, setCollapsedState] = useState(() => readLS(LS_COLLAPSED, false));
  const [expandedSections, setExpanded] = useState(() =>
    readLS(LS_EXPANDED, { documents: true })
  );

  const [mobileOpen, setMobileOpen]   = useState(false);
  const [activePanel, setActivePanel] = useState(null); // "notif" | "search" | "profile"
  const [currentTime, setCurrentTime] = useState(new Date());
  const [unreadCount, setUnreadCount] = useState(0);

  const notifRef   = useRef(null);
  const searchRef  = useRef(null);
  const profileRef = useRef(null);
  const mobileDrawerRef = useRef(null);

  const {
    admin, company, businessLogoUrl, bizId,
    notifications, businessTimezone, formatNotificationTime,
  } = useSidebarData(setUnreadCount);

  const setCollapsed = useCallback((v) => {
    setCollapsedState(v);
    writeLS(LS_COLLAPSED, v);
    onCollapsedChange?.(v);
  }, [onCollapsedChange]);

  // Live clock — 30s tick
  useEffect(() => {
    const tick = setInterval(() => setCurrentTime(new Date()), 30000);
    return () => clearInterval(tick);
  }, []);

  // Click/touch-away + Escape
  useEffect(() => {
    const dismiss = (e) => {
      if (
        notifRef.current?.contains(e.target) ||
        searchRef.current?.contains(e.target) ||
        profileRef.current?.contains(e.target)
      ) return;
      setActivePanel(null);
    };
    const onKey = (e) => {
      if (e.key === "Escape") {
        setActivePanel(null);
        setMobileOpen(false);
      }
    };
    document.addEventListener("mousedown", dismiss);
    document.addEventListener("touchstart", dismiss, { passive: true });
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", dismiss);
      document.removeEventListener("touchstart", dismiss);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  // Keyboard shortcuts: ⌘K opens search, ⌘B toggles collapse
  useEffect(() => {
    const onKey = (e) => {
      const mod = e.metaKey || e.ctrlKey;
      if (mod && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setActivePanel((p) => (p === "search" ? null : "search"));
      } else if (mod && e.key.toLowerCase() === "b") {
        e.preventDefault();
        setCollapsed(!collapsed);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [collapsed, setCollapsed]);

  // Body scroll lock for mobile drawer
  useEffect(() => {
    if (!mobileOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [mobileOpen]);

  // Focus first focusable when mobile drawer opens
  useEffect(() => {
    if (mobileOpen) {
      requestAnimationFrame(() => {
        const first = mobileDrawerRef.current?.querySelector("button, a");
        first?.focus();
      });
    }
  }, [mobileOpen]);

  const togglePanel = useCallback((p) => setActivePanel((x) => (x === p ? null : p)), []);
  const closePanel  = useCallback(() => setActivePanel(null), []);
  const closeMobile = useCallback(() => setMobileOpen(false), []);
  const noop        = useCallback(() => {}, []);

  const toggleSection = useCallback((sectionKey) => {
    setExpanded((prev) => {
      const next = { ...prev, [sectionKey]: !prev[sectionKey] };
      writeLS(LS_EXPANDED, next);
      return next;
    });
  }, []);

  const handleLogout = useCallback(async () => {
    setUnreadCount(0);
    await logout();
  }, [logout]);

  const unreadNotifCount = notifications.filter((n) => !n.read).length;

  // ── Localized strings bundle (with English fallbacks) ───────
  const sidebarStrings = useMemo(() => ({
    tagline:        t("navigation:businessManagement", "Business Management"),
    plan:           t("navigation:plan.plan", "Plan"),
    upgrade:        t("navigation:plan.upgrade", "Upgrade"),
    subscription:   t("navigation:quickLinks.subscription", "Subscription"),
    support:        t("navigation:quickLinks.support", "Support"),
    gettingStarted: t("navigation:quickLinks.gettingStarted", "Getting Started"),
    training:       t("navigation:quickLinks.training", "Training"),
    administrator:  t("navigation:profile.administrator", "Administrator"),
    comingSoon:     t("navigation:comingSoon", "Soon"),
  }), [t]);

  const notifStrings = useMemo(() => ({
    title:   t("navigation:topBar.notifications", "Notifications"),
    unread:  t("navigation:topBar.unreadShort", "unread"),
    markAll: t("navigation:topBar.markAll", "Mark all read"),
    empty:   t("navigation:topBar.noNotifications", "No notifications yet"),
    viewAll: t("navigation:topBar.viewAll", "View all notifications"),
  }), [t]);

  const searchStrings = useMemo(() => ({
    placeholder:  t("navigation:topBar.search", "Search pages, features…"),
    noResults:    t("navigation:topBar.noResults", 'No results for "{q}"'),
    quickAccess:  t("navigation:topBar.quickAccess", "Quick Access"),
    quickLabels:  ["Dashboard", "Invoices", "Schedule", "Customers", "Reports", "Employees"],
  }), [t]);

  const profileStrings = useMemo(() => ({
    administrator: t("navigation:profile.administrator", "Administrator"),
    logout:        t("navigation:profile.logout", "Log Out"),
    items: [
      { icon: FiUser,       label: t("navigation:profile.myProfile", "My Profile"),             to: "/admin/admin-profile" },
      { icon: FiBriefcase,  label: t("navigation:profile.businessProfile", "Business Profile"), to: "/admin/branding" },
      { icon: FiCreditCard, label: t("navigation:profile.billing", "Billing & Plans"),          to: "/admin/subscriptions" },
      { icon: FiSettings,   label: t("navigation:profile.settingsLink", "Settings"),            to: "/admin/settings" },
    ],
  }), [t]);

  // ── Nav structure ──────────────────────────────────────────
  const NAV_GROUPS = useMemo(() => [
    {
      key: null,
      group: null,
      items: [
        { to: "/admin/dashboard",         icon: FiHome,          label: t("navigation:dashboard") },
        { to: "/admin/insights",          icon: FiZap,           label: t("navigation:insights", "Insights"), feature: FEATURES.BASIC_INSIGHTS },
        { to: "/admin/schedule",          icon: FiCalendar,      label: t("navigation:schedule") },
        { to: "/admin/tasks",             icon: FiCheckSquare,   label: t("navigation:tasks") },
        { to: "/admin/messaging",         icon: FiMessageSquare, label: t("navigation:messages"), badge: unreadCount },
        { to: "/admin/customer-messages", icon: FiMail,          label: t("navigation:customerMessages") },
        { to: "/admin/my-website",        icon: FiGlobe,         label: t("navigation:myWebsite") },
        { to: "/admin/marketing",         icon: FiTrendingUp,    label: t("navigation:marketing") },
      ],
    },
    {
      key: "team",
      group: t("navigation:sections.team"),
      icon: FiUsers,
      items: [
        { to: "/admin/customers",         icon: FiBriefcase, label: t("navigation:team.customers") },
        { to: "/admin/employees",         icon: FiUsers,     label: t("navigation:team.employees") },
        { to: "/admin/shift-schedule",    icon: FiGrid,      label: t("navigation:team.shiftSchedule", "Shift Schedule") },
        { to: "/admin/location-tracking", icon: FiMapPin,    label: t("navigation:team.locationTracking"), feature: FEATURES.LOCATION_TRACKING },
      ],
    },
    {
      key: "documents",
      group: t("navigation:sections.documents"),
      icon: FiFileText,
      items: [
        { to: "/admin/invoices",          icon: FiFileText,   label: t("navigation:documents.invoices") },
        { to: "/admin/estimates",         icon: FiClipboard,  label: t("navigation:documents.estimates") },
        { to: "/admin/bookings",          icon: FiBook,       label: t("navigation:documents.bookings") },
        { to: "/admin/requests",          icon: FiInbox,      label: t("navigation:documents.requests") },
        { to: "/admin/contracts",         icon: FiEdit,       label: t("navigation:documents.contracts") },
        { to: "/admin/quotes",            icon: FiTag,        label: t("navigation:documents.quotes") },
        { to: "/admin/proposals",         icon: FiBriefcase,  label: t("navigation:documents.proposals") },
        { to: "/admin/bills-of-sale",     icon: FiDollarSign, label: t("navigation:documents.billsOfSale") },
        { to: "/admin/signed-documents",  icon: FiCheckSquare,label: t("navigation:documents.signedDocuments") },
        { to: "/admin/recurring-jobs",    icon: FiRepeat,     label: t("navigation:documents.recurringJobs"), feature: FEATURES.RECURRING_JOBS },
        { to: "/admin/bulk-schedule",     icon: FiCalendar,   label: t("navigation:documents.bulkSchedule"), feature: FEATURES.RECURRING_JOBS },
      ],
    },
    {
      key: "operations",
      group: t("navigation:sections.operations"),
      icon: FiPackage,
      items: [
        { to: "/admin/produce",   icon: FiPackage, label: t("navigation:operations.products") },
        { to: "/admin/services",  icon: FiTarget,  label: t("navigation:operations.services") },
        { to: "/admin/suppliers", icon: FiTruck,   label: t("navigation:operations.suppliers") },
      ],
    },
    {
      key: "finance",
      group: t("navigation:sections.finance"),
      icon: FiDollarSign,
      items: [
        { to: "/admin/expenses",     icon: FiDollarSign, label: t("navigation:finance.expenses"),     feature: FEATURES.EXPENSE_TRACKING },
        { to: "/admin/transactions", icon: FiTag,        label: t("navigation:finance.transactions") },
        { to: "/admin/timesheet",    icon: FiClock,      label: t("navigation:finance.timesheet"),    feature: FEATURES.TIME_TRACKING },
        { to: "/admin/payments",     icon: FiCreditCard, label: t("navigation:finance.payments") },
        { to: "/admin/payroll",      icon: FiUsers,      label: t("navigation:finance.payroll"),      feature: FEATURES.TIME_TRACKING },
        { to: "/admin/reports",      icon: FiLayers,     label: t("navigation:finance.reports") },
      ],
    },
    {
      key: "settings",
      group: t("navigation:sections.settings"),
      icon: FiSettings,
      items: [
        { to: "/admin/settings",                icon: FiSettings,     label: t("navigation:settings.settings") },
        { to: "/admin/availability",            icon: FiCalendar,     label: t("navigation:settings.availability") },
        { to: "/admin/branding",                icon: FiGrid,         label: t("navigation:settings.branding"),      feature: FEATURES.CUSTOM_BRANDING },
        { to: "/admin/team-management",         icon: FiUsers,        label: t("navigation:settings.teamManagement"),feature: FEATURES.TEAM_MANAGEMENT },
        { to: "/admin/automations",             icon: FiZap,          label: t("navigation:settings.automations"),   feature: FEATURES.AUTOMATIONS },
        { to: "/admin/integrations",            icon: FiLink,         label: t("navigation:settings.integrations") },
        { to: "/admin/security",                icon: FiShield,       label: t("navigation:settings.security") },
        { to: "/admin/data-export",             icon: FiDownloadCloud,label: t("navigation:settings.dataExport"),    feature: FEATURES.DATA_IMPORT },
      ],
    },
  ], [t, unreadCount]);

  const sharedProps = {
    navGroups: NAV_GROUPS,
    expandedSections,
    toggleSection,
    admin,
    company,
    businessLogoUrl,
    plan,
    planDisplayName,
    hasAccess,
    strings: sidebarStrings,
  };

  // ── RENDER ─────────────────────────────────────────────────
  return (
    <>
      {/* Webkit scrollbar styles for the sidebar */}
      <style>{`
        .sidebar-scroll { scrollbar-width: thin; scrollbar-color: #E5E7EB transparent; }
        .sidebar-scroll::-webkit-scrollbar { width: 6px; }
        .sidebar-scroll::-webkit-scrollbar-track { background: transparent; }
        .sidebar-scroll::-webkit-scrollbar-thumb { background-color: rgba(148,163,184,0.35); border-radius: 9999px; }
        .sidebar-scroll::-webkit-scrollbar-thumb:hover { background-color: rgba(148,163,184,0.6); }
        .dark .sidebar-scroll::-webkit-scrollbar-thumb { background-color: rgba(71,85,105,0.55); }
      `}</style>

      {/* ── DESKTOP SIDEBAR ── */}
      <aside
        aria-label="Sidebar"
        className={`hidden lg:flex flex-col fixed left-0 top-0 h-screen bg-white dark:bg-slate-900 border-r border-slate-100 dark:border-slate-800 z-40 transition-all duration-300 shadow-[2px_0_16px_rgba(0,0,0,.04)] dark:shadow-[2px_0_16px_rgba(0,0,0,.4)] overflow-visible ${
          collapsed ? "w-[68px]" : "w-60"
        }`}
      >
        {/* Collapse toggle — larger target */}
        <button
          onClick={() => setCollapsed(!collapsed)}
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          title={`${collapsed ? "Expand" : "Collapse"} (⌘B)`}
          className="absolute -right-3 top-[72px] w-6 h-6 rounded-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow flex items-center justify-center text-slate-500 dark:text-slate-300 hover:text-slate-800 dark:hover:text-white hover:scale-110 transition-all z-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-400"
        >
          <FiChevronRight
            className={`w-3.5 h-3.5 transition-transform duration-200 ${collapsed ? "" : "rotate-180"}`}
          />
        </button>

        <SidebarContent
          {...sharedProps}
          collapsed={collapsed}
          onClose={noop}
        />
      </aside>

      {/* ── TOP BAR ── */}
      <header
        className={`fixed top-0 right-0 h-14 bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl border-b border-slate-100 dark:border-slate-800 z-30 flex items-center justify-between px-4 lg:px-5 transition-all duration-300 ${
          collapsed ? "lg:left-[68px]" : "lg:left-60"
        } left-0`}
      >
        {/* Mobile: hamburger + logo */}
        <div className="flex items-center gap-3 lg:hidden">
          <button
            onClick={() => setMobileOpen(true)}
            aria-label="Open menu"
            className="p-2 rounded-xl text-slate-500 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-all"
          >
            <FiMenu className="w-5 h-5" />
          </button>
          <span className="font-black text-[15px] text-[#0D3B66] dark:text-teal-300">{company || "Effi-Track"}</span>
        </div>

        <div className="hidden lg:block" />

        {/* Right cluster */}
        <div className="flex items-center gap-2">
          {/* Clock */}
          <div className="hidden md:flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-slate-100 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-200">
            <FiClock className="w-3.5 h-3.5 text-teal-500" />
            <span className="text-[12px] font-semibold tabular-nums">
              {currentTime.toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
                timeZone: businessTimezone,
              })}
            </span>
          </div>

          <div className="hidden sm:block">
            <LanguageSelector compact />
          </div>

          <ThemeToggle
            className="w-8 h-8 rounded-xl border flex items-center justify-center transition-colors border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 dark:border-amber-400/40 dark:bg-amber-400/10 dark:text-amber-300 dark:hover:bg-amber-400/20"
            iconClassName="w-4 h-4"
          />

          {/* Search */}
          <div ref={searchRef} className="relative">
            <button
              onClick={() => togglePanel("search")}
              title="Search (⌘K)"
              aria-label="Search"
              className={`w-8 h-8 rounded-xl border border-slate-100 dark:border-slate-700 flex items-center justify-center transition-all ${
                activePanel === "search"
                  ? "bg-slate-100 dark:bg-slate-800 text-slate-800 dark:text-slate-100"
                  : "bg-slate-50 dark:bg-slate-800 text-slate-500 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700"
              }`}
            >
              <FiSearch className="w-4 h-4" />
            </button>
            {activePanel === "search" && (
              <SearchPanel
                navGroups={NAV_GROUPS}
                onClose={closePanel}
                strings={searchStrings}
              />
            )}
          </div>

          {/* Messages quick-link with count */}
          <button
            onClick={() => navigate("/admin/messaging")}
            aria-label={t("navigation:messages")}
            title={t("navigation:messages")}
            className="relative w-8 h-8 rounded-xl border border-slate-100 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-500 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center justify-center transition-all"
          >
            <FiMessageSquare className="w-4 h-4" />
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 min-w-[16px] h-4 rounded-full bg-teal-500 text-white text-[9px] font-bold flex items-center justify-center border-2 border-white dark:border-slate-900 px-0.5">
                {unreadCount > 9 ? "9+" : unreadCount}
              </span>
            )}
          </button>

          {/* Notifications */}
          <div ref={notifRef} className="relative">
            <button
              onClick={() => togglePanel("notif")}
              aria-label={notifStrings.title}
              className={`w-8 h-8 rounded-xl border border-slate-100 dark:border-slate-700 flex items-center justify-center transition-all relative ${
                activePanel === "notif"
                  ? "bg-slate-100 dark:bg-slate-800 text-slate-800 dark:text-slate-100"
                  : "bg-slate-50 dark:bg-slate-800 text-slate-500 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700"
              }`}
            >
              <FiBell className="w-4 h-4" />
              {unreadNotifCount > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[16px] h-4 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center border-2 border-white dark:border-slate-900 px-0.5">
                  {unreadNotifCount > 9 ? "9+" : unreadNotifCount}
                </span>
              )}
            </button>
            {activePanel === "notif" && (
              <NotificationPanel
                notifications={notifications}
                bizId={bizId}
                formatTime={formatNotificationTime}
                onClose={closePanel}
                onNavigate={navigate}
                strings={notifStrings}
              />
            )}
          </div>

          {/* Profile */}
          <div ref={profileRef} className="relative">
            <button
              onClick={() => togglePanel("profile")}
              aria-haspopup="menu"
              aria-expanded={activePanel === "profile"}
              className={`flex items-center gap-2 pl-1.5 pr-2.5 py-1 rounded-xl border border-slate-100 dark:border-slate-700 transition-all ${
                activePanel === "profile"
                  ? "bg-slate-100 dark:bg-slate-800"
                  : "bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700"
              }`}
            >
              <img
                src={admin?.profilePicture || BusinessLogo}
                alt="Profile"
                className="w-8 h-8 rounded-full border-2 border-emerald-200 dark:border-emerald-500/40 bg-white"
              />
              <span className="hidden sm:block text-[12px] font-semibold text-slate-700 dark:text-slate-200 max-w-[90px] truncate">
                {admin?.name?.split(" ")[0] || company?.split(" ")[0] || "Admin"}
              </span>
              <FiChevronDown className={`w-3.5 h-3.5 text-slate-400 dark:text-slate-500 transition-transform ${activePanel === "profile" ? "rotate-180" : ""}`} />
            </button>
            {activePanel === "profile" && (
              <ProfileMenu
                admin={admin}
                businessLogoUrl={businessLogoUrl}
                onClose={closePanel}
                onLogout={handleLogout}
                strings={profileStrings}
              />
            )}
          </div>
        </div>
      </header>

      {/* ── MOBILE OVERLAY ── */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40 lg:hidden"
          onClick={closeMobile}
        />
      )}

      {/* ── MOBILE SIDEBAR ── */}
      <aside
        ref={mobileDrawerRef}
        aria-label="Sidebar"
        aria-hidden={!mobileOpen}
        className={`fixed left-0 top-0 h-screen w-64 bg-white dark:bg-slate-900 shadow-2xl z-50 transform transition-transform duration-300 ease-in-out lg:hidden ${
          mobileOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <button
          onClick={closeMobile}
          aria-label="Close menu"
          className="absolute top-3.5 right-3.5 p-1.5 rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 transition-all z-10"
        >
          <FiX className="w-4 h-4" />
        </button>
        <SidebarContent
          {...sharedProps}
          collapsed={false}
          onClose={closeMobile}
        />
      </aside>
    </>
  );
};

export default LeftSidebar;
